import { Banner, BannerItem } from '@athena/forge';
import { getLocalizedString } from '../../utils/i18n-utils';
import { useWidgetContext } from '../context/WidgetContext';
import { INFO_BANNER_MESSAGE_KEY_MAP_V2 } from '../../constants';
import ForgotPasswordLink from './ForgotPasswordLink';
import athenaConfig from '../../config';
import { getPasswordResetUrlByClientId } from '../../utils/app-utils';
import AmplitudeUtils from '../../utils/amplitude-utils';
import Link from '../common/Link';

export default function UserLockedOutBanner() {
  const {
    settings: { clientId, isAuthnWithLwaClientId, isDegradedState },
  } = useWidgetContext();

  // 1st scenario: LWA users
  if (isAuthnWithLwaClientId) {
    const HEADER_TEXT = getLocalizedString('error.accountblocked.lwa.header');
    const DESCRIPTION_PART1 = getLocalizedString('error.accountblocked.lwa.description.part1');
    const LINK_TEXT = getLocalizedString('error.accountblocked.lwa.description.link');
    const DESCRIPTION_PART2 = getLocalizedString('error.accountblocked.lwa.description.part2');

    return (
      <Banner alertType='critical' className='fe_u_padding--small'>
        <BannerItem headerText={HEADER_TEXT}>
          {DESCRIPTION_PART1}
          <ForgotPasswordLink text={LINK_TEXT} />
          {DESCRIPTION_PART2}
        </BannerItem>
      </Banner>
    );
  }

  // 2nd scenario: Degraded state check
  else if (isDegradedState) {
    const HEADER_TEXT = getLocalizedString('error.accountblocked.degraded.header');
    const HEADER_DESCRIPTION = getLocalizedString('error.accountblocked.degraded.description');

    return (
      <Banner alertType='critical' className='fe_u_padding--small'>
        <BannerItem headerText={HEADER_TEXT}>
          {HEADER_DESCRIPTION}
        </BannerItem>
      </Banner>
    );
  }

  // 3rd scenario: (AthenaOne web clients)
  else {
    const { enableNativePasswordReset } = athenaConfig.featureFlags;

    // If feature flag is not enabled, use the existing static message
    if (!enableNativePasswordReset) {
      const HEADER_TEXT = getLocalizedString(INFO_BANNER_MESSAGE_KEY_MAP_V2['ERROR']['ACCOUNTBLOCKED'].header);
      const HEADER_DESCRIPTION = getLocalizedString(INFO_BANNER_MESSAGE_KEY_MAP_V2['ERROR']['ACCOUNTBLOCKED'].description);
      return (
        <Banner alertType='critical' className='fe_u_padding--small'>
          <BannerItem headerText={HEADER_TEXT}>
            {HEADER_DESCRIPTION}
          </BannerItem>
        </Banner>
      );
    }
    else {
      const HEADER_TEXT = getLocalizedString('error.resetpasswordblockeduser.header');
      const HEADER_DESCRIPTION = getLocalizedString('error.resetpasswordblockeduser.description');
      const parts = HEADER_DESCRIPTION.split('reset your password');

      const handleResetPasswordClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        await AmplitudeUtils.logCustomEvent('AccountBlocked_ResetPasswordLinkClick', {});
        const $forgotPasswordButton = jQueryCourage('#athena-forgot-password-link button');
        if ($forgotPasswordButton.length > 0) {
          $forgotPasswordButton.trigger('click');
          return;
        }

        // Fallback when the forgot password button element is not found. The legacy
        // athenaNet resetpassword.esp page has been retired (UIAMUA-8419), so route to
        // the OHSW-hosted reset page instead.
        const passwordResetUrl = getPasswordResetUrlByClientId(clientId);
        if (passwordResetUrl) {
          window.location.assign(passwordResetUrl);
        }
        else {
          await AmplitudeUtils.logCustomEvent('AccountBlocked_ResetPasswordLinkUnavailable', { clientId });
        }
      };

      return (
        <Banner alertType='critical' className='fe_u_padding--small'>
          <BannerItem headerText={HEADER_TEXT}>
            {parts[0]}
            <Link text={'reset your password'} onClick={handleResetPasswordClick} />
            {parts[1]}
          </BannerItem>
        </Banner>
      );
    }
  }
}
